import { roundWeightGrams } from "./gold.js";
/**
 * The one place a state's name is written. Surfaces that need to name a state without a
 * delivery in hand — a status filter, say — read it from here rather than restating it.
 */
export const DELIVERY_STATE_LABEL = {
    PAID: "Utbetalt",
    ARCHIVED: "Avsluttet",
    RETURNED: "Returnert",
    ACCEPTED: "Godkjent",
    DECLINED: "Avslått",
    OFFER_SENT: "Tilbud klart",
    ASSESSING: "Vurderes",
    RECEIVED: "Mottatt og veid",
    AT_STORE: "Konvolutt mottatt",
    POUCH_ORDERED: "Konvolutt bestilt",
    REGISTERED: "Registrert",
    WAITING: "Mottatt",
};
export function deliveryOfferIsVisible(source) {
    return (source.assessmentOfferSentAt != null ||
        source.assessmentResponse != null ||
        source.paidAt != null ||
        source.transactionId != null);
}
/**
 * Whether the offer is past changing: the customer has answered it, or the money it set moving
 * exists. Settling always passes through one of the first two; `paidAt` is kept so a delivery
 * settled outside both still counts.
 */
export function deliveryOfferIsSettled(source) {
    return source.assessmentResponse != null || source.transactionId != null || source.paidAt != null;
}
/**
 * Whether a delivery has been weighed on arrival. Stricter than the timestamp
 * `deliveryStateSource` reads: a recorded zero is not a weight, and an offer must
 * never be made against one.
 */
export function hasRecordedReceivedWeight(input) {
    return typeof input.receivedWeightGrams === "number" && input.receivedWeightGrams > 0;
}
export function summarizeDeliveryState(source) {
    // Every payout also archives the delivery, so paid is asked first: ARCHIVED is left meaning a
    // delivery closed without payment (owner's ruling, 2026-10-08).
    if (source.paid) {
        return {
            code: "PAID",
            label: DELIVERY_STATE_LABEL.PAID,
            message: source.giftCard
                ? "Innleveringen er ferdig behandlet og gavekortet er registrert."
                : "Innleveringen er ferdig behandlet og utbetalingen er registrert.",
        };
    }
    if (source.archived) {
        return {
            code: "ARCHIVED",
            label: DELIVERY_STATE_LABEL.ARCHIVED,
            message: "Innleveringen er avsluttet. Kontakt David-Andersen hvis dette ikke stemmer.",
        };
    }
    if (source.returned) {
        return {
            code: "RETURNED",
            label: DELIVERY_STATE_LABEL.RETURNED,
            message: "Innleveringen er sendt tilbake til deg.",
        };
    }
    if (source.response === "ACCEPTED") {
        return {
            code: "ACCEPTED",
            label: DELIVERY_STATE_LABEL.ACCEPTED,
            message: source.awaitingPayout
                ? "Tilbudet er godkjent og venter på manuell utbetaling."
                : "Tilbudet er godkjent og klargjøres for utbetaling.",
        };
    }
    if (source.response === "DECLINED") {
        return {
            code: "DECLINED",
            label: DELIVERY_STATE_LABEL.DECLINED,
            message: "Tilbudet er avslått, og innleveringen håndteres for retur.",
        };
    }
    if (source.offerVisible) {
        return {
            code: "OFFER_SENT",
            label: DELIVERY_STATE_LABEL.OFFER_SENT,
            message: "Vi har sendt deg et tilbud på hele innleveringen.",
        };
    }
    if (source.hasAssessmentRows) {
        return {
            code: "ASSESSING",
            label: DELIVERY_STATE_LABEL.ASSESSING,
            message: "Gjenstandene dine er registrert og vurderes av en gullsmed.",
        };
    }
    // Weighing is what the customer can check against their own receipt, so it
    // outranks mere arrival — and must never be claimed before it has happened.
    if (source.weightRecorded) {
        return {
            code: "RECEIVED",
            label: DELIVERY_STATE_LABEL.RECEIVED,
            message: "Vi har registrert vekten av gjenstandene dine. En gullsmed vurderer gjenstandene dine.",
        };
    }
    if (source.pouchArrived) {
        return {
            code: "AT_STORE",
            label: DELIVERY_STATE_LABEL.AT_STORE,
            message: "Vi har mottatt konvolutten din. En gullsmed veier og vurderer gjenstandene.",
        };
    }
    if (source.pouchOrdered) {
        return {
            code: "POUCH_ORDERED",
            label: DELIVERY_STATE_LABEL.POUCH_ORDERED,
            message: "Gullkonvolutten din er på vei i posten. Fyll den med gjenstandene du vil selge og send den tilbake til oss.",
        };
    }
    if (source.queueRegistered) {
        return {
            code: "REGISTERED",
            label: DELIVERY_STATE_LABEL.REGISTERED,
            message: "Innleveringen er registrert og venter på videre behandling.",
        };
    }
    return {
        code: "WAITING",
        label: DELIVERY_STATE_LABEL.WAITING,
        message: "Innleveringen er mottatt og venter på registrering.",
    };
}
const QUEUE_REGISTERED_STATUSES = new Set(["COMPLETED", "SERVING", "CALLED"]);
/**
 * The one adapter from a delivery to the customer-facing vocabulary. Both kinds read the
 * same fields for the same situation; only the postal leg is asked about the delivery method.
 */
export function deliveryStateSource(delivery, queueStatus) {
    const mailLeg = delivery.deliveryMethod === "MAIL" ? delivery.mailLegStatus : null;
    return {
        archived: delivery.status === "ARCHIVED",
        returned: mailLeg === "RETURNED_TO_CUSTOMER",
        paid: delivery.paidAt != null,
        giftCard: delivery.assessmentSettlementType === "GIFT_CARD",
        response: delivery.assessmentResponse ?? null,
        awaitingPayout: delivery.transactionId != null,
        offerVisible: deliveryOfferIsVisible(delivery),
        hasAssessmentRows: (delivery.assessmentRows?.length ?? 0) > 0,
        weightRecorded: delivery.receivedWeightRecordedAt != null,
        pouchArrived: mailLeg === "RECEIVED_AT_STORE",
        // REQUESTED covers the whole outbound window, pouch out and gold coming back — see MailLegStatus.
        pouchOrdered: mailLeg === "REQUESTED",
        queueRegistered: delivery.deliveryMethod === "STORE" &&
            queueStatus != null &&
            QUEUE_REGISTERED_STATUSES.has(queueStatus),
    };
}
/**
 * Brukthandelloven's holding period before purchased gold may be resold. It runs from the
 * purchase — the payout, which is when David-Andersen owns the gold — not from the customer's
 * acceptance and not from when the gold was handed in (owner's ruling, 2026-09-17).
 * See `docs/superpowers/specs/2026-09-17-smelting-design.md`.
 */
export const SMELTING_HOLD_DAYS = 14;
/** One day in milliseconds, for the day-counting rules that live around a delivery. */
export const DAY_MS = 24 * 60 * 60_000;
/**
 * Whether a delivery may go into a refining round, and if not, why. The server refuses on this
 * and the overview table renders from it, so the two cannot disagree. The pointer is the whole
 * answer to "is it in a round": how far that round has come is the round's own status, not the
 * delivery's (owner's ruling, 2026-09-18).
 */
export function smeltingEligibility(source, now) {
    if (source.paidAt == null)
        return { code: "NOT_PAID" };
    // Null or absent alike: gold paid before smelting existed has no pointer and is as eligible as
    // any other (owner, 2026-09-18).
    if (source.refiningRoundId != null)
        return { code: "IN_ROUND", roundId: source.refiningRoundId };
    const eligibleAt = source.paidAt + SMELTING_HOLD_DAYS * DAY_MS;
    if (now < eligibleAt)
        return { code: "ON_HOLD", eligibleAt };
    return { code: "ELIGIBLE" };
}
/** The gold weight the offer was made on: every assessment row, whatever its karat. */
export function deliveryGoldWeightGrams(source) {
    return roundWeightGrams((source.assessmentRows ?? []).reduce((sum, row) => sum + row.weightGrams, 0));
}
/** Distinct karats on the delivery, highest first. */
export function deliveryKarats(source) {
    return [...new Set((source.assessmentRows ?? []).map((row) => row.karat))].sort((a, b) => b - a);
}
